import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import debounce from "lodash.debounce";
import {loadPlacesLibrary} from "../../api/GoogleMapsLoader";

const MIN_INPUT_LENGTH = 3;
const MAX_SUGGESTIONS = 5;
const ADDRESS_TYPES = ["street_address", "route", "premise", "subpremise"];

/**
 * Address suggestions from Google Places Autocomplete (New) for the given country.
 * <p>
 * All requests until a suggestion is selected share one session token, so Google bills them as one session
 * instead of every keystroke. The session ends with loading the details of the selected place.
 */
export default function useAddressAutocomplete(country) {
    const [places, setPlaces] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const sessionToken = useRef(null);
    const latestInput = useRef("");

    useEffect(() => {
        let mounted = true;
        loadPlacesLibrary().then(library => {
            if (mounted) {
                setPlaces(library);
            }
        });
        return () => {
            mounted = false;
        };
    }, []);

    const fetchSuggestions = useMemo(() => debounce(async (input, regionCode) => {
        if (!places) {
            return;
        }
        if (!sessionToken.current) {
            sessionToken.current = new places.AutocompleteSessionToken();
        }
        try {
            const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: input,
                sessionToken: sessionToken.current,
                includedRegionCodes: [regionCode.toLowerCase()],
                includedPrimaryTypes: ADDRESS_TYPES,
                language: "de",
                region: regionCode.toLowerCase(),
            });
            //ignore answers to an input the customer has typed further in the meantime
            if (latestInput.current !== input) {
                return;
            }
            setSuggestions(response.suggestions
                .map(suggestion => suggestion.placePrediction)
                .filter(prediction => prediction != null)
                .slice(0, MAX_SUGGESTIONS));
        } catch (error) {
            console.warn("Loading address suggestions failed:", error);
            setSuggestions([]);
        }
    }, 250), [places]);

    useEffect(() => () => fetchSuggestions.cancel(), [fetchSuggestions]);

    const search = useCallback((input) => {
        latestInput.current = input;
        if (!places || input.trim().length < MIN_INPUT_LENGTH) {
            fetchSuggestions.cancel();
            setSuggestions([]);
            return;
        }
        fetchSuggestions(input, country);
    }, [places, country, fetchSuggestions]);

    const clearSuggestions = useCallback(() => {
        fetchSuggestions.cancel();
        setSuggestions([]);
    }, [fetchSuggestions]);

    /**
     * Loads the address of the selected suggestion. Resolves to the address fields Google knows, e.g. a selected
     * street has no house number.
     */
    const selectSuggestion = useCallback(async (prediction) => {
        clearSuggestions();
        const place = prediction.toPlace();
        try {
            await place.fetchFields({fields: ["addressComponents"]});
        } finally {
            sessionToken.current = null;
        }
        return toAddress(place.addressComponents || []);
    }, [clearSuggestions]);

    return {
        available: places != null,
        suggestions,
        search,
        clearSuggestions,
        selectSuggestion,
    };
}

function toAddress(addressComponents) {
    const component = (...types) => addressComponents.find(c => types.some(type => c.types.includes(type)));
    const address = {
        street: component("route")?.longText,
        houseNumber: component("street_number")?.longText,
        zip: component("postal_code")?.longText,
        city: component("locality", "postal_town")?.longText,
        country: component("country")?.shortText,
    };
    //only the fields Google knows, so a selected street keeps an already entered house number
    return Object.fromEntries(Object.entries(address).filter(([, value]) => value));
}
